import React, { useState } from "react";
import { DeviceEventEmitter, View } from "react-native";
import tw from "twrnc";

import { notify } from "@/components/shared/AppToast";
import { useActionSheet } from "@/components/hooks/useActionSheet";
import { FieldLabel, Hint } from "@/components/settings/Field";
import { NavRow } from "@/components/settings/SettingRow";
import { t, tn } from "@/localization";
import { choosePayoutAccount } from "../http/stripeLinks";
import { accountsToOffer, asksBeforeUsing } from "./choices";

// Sell Tickets for the holder of a business account, on an event with no
// payout account: the Stripe accounts their other events already use, each
// one tap instead of connecting Stripe again. `payouts` is
// usePayoutAccounts(); with no accounts this shows nothing and Sell Tickets
// looks as it always did.

// What we know of whose an account is, and how many events it pays for.
export function accountDescription(found) {
  const count = found.eventCount || 0;
  if (found.kind === "client")
    return count
      ? tn(
          "Your client's account · used for {count} event",
          "Your client's account · used for {count} events",
          count
        )
      : t("Your client's account");
  if (found.kind === "own")
    return count
      ? tn(
          "Your account · used for {count} event",
          "Your account · used for {count} events",
          count
        )
      : t("Your account");
  return count
    ? tn("Used for {count} event", "Used for {count} events", count)
    : t("Connected before");
}

const confirm = (showActionSheetWithOptions, { title, button }) =>
  new Promise((resolve) =>
    showActionSheetWithOptions(
      { options: [button, t("Cancel")], cancelButtonIndex: 1, title },
      (index) => resolve(index === 0)
    )
  );

export const SavedAccountChoices = ({ event, payouts }) => {
  const { showActionSheetWithOptions } = useActionSheet();
  const [busy, setBusy] = useState(null);
  if (!payouts.data?.business) return null;
  const offered = accountsToOffer(payouts.data);
  if (!offered.length) return null;

  const use = async (found) => {
    if (asksBeforeUsing(found)) {
      const yes = await confirm(showActionSheetWithOptions, {
        title:
          found.kind === "client"
            ? t(
                "Ticket payouts for “{event}” will go to {account}, your client's Stripe account. Use it only for this client's events.",
                { event: event.title, account: found.label }
              )
            : t("Ticket payouts for “{event}” will go to {account}.", {
                event: event.title,
                account: found.label,
              }),
        button: t("Use this account"),
      });
      if (!yes) return;
    }
    setBusy(found.account);
    try {
      await choosePayoutAccount(found.account, event.slug);
      notify.success(t("Stripe account connected"), found.label);
      // evt.live wrote the event: load it again.
      DeviceEventEmitter.emit("refresh_event");
      payouts.reload();
    } catch (error) {
      notify.error(t("Couldn't use this Stripe account"), error.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <View style={tw`mb-5`}>
      <FieldLabel
        label={t("Use a Stripe account you've connected before")}
        help="tickets.savedAccounts"
      />
      <View style={tw`gap-2`}>
        {offered.map((found) => (
          <NavRow
            key={found.account}
            icon={found.kind === "client" ? "people-outline" : "card-outline"}
            label={found.label}
            description={
              busy === found.account
                ? t("Connecting…")
                : accountDescription(found)
            }
            onPress={() => !busy && use(found)}
          />
        ))}
      </View>
      <Hint style={tw`mt-3`}>{t("Or connect another Stripe account:")}</Hint>
    </View>
  );
};
